// Taşeron portalı — Belge Analizi inceleme paneli: Kişi seçici (plan S25 C7/C9/D9,
// `docs/plans/S25-analysis-inbox.md`; tasarım incelemesi §3.2). Düz `@radix-ui/react-select`
// (`packages/shared`'da zaten kurulu, `LanguageSwitcher.tsx` kullanıyor) — ARAMA KUTUSU YOK:
// tipik taşeronda personel sayısı ~20'nin altında, filtreleme maliyeti karşılığını vermiyor (D9).
//
// **Neden app-local.** `packages/shared/src/ui/Select.tsx` yerli `<select>`dir ve öyle KALIR —
// bu bileşen iş anlamı taşıdığı için (`createOption`, C9) `Badge`in izlediği emsalle AYNI:
// app-local.
//
// **C9 — `createOption` (tasarım §3.2).** Verilirse listenin ALTINA bir `Select.Separator` +
// sentinel-değerli bir `Select.Item` eklenir ("+ Yeni personel oluştur"). Sentinel `onChange`e
// ASLA ULAŞMAZ — `onValueChange` içinde YAKALANIR ve `createOption.onSelect()` çağrılır; `value`
// hiçbir zaman sentinel'e EŞİT OLMAZ, yani seçili kapalı kontrol asla "+ Yeni personel
// oluştur" göstermez. Çağıran (`AnalysisReviewPane`) bunu yalnız KAYIT DIŞI kişide geçirir.

import { type SelectOption, cn } from '@isgportal/shared';
import * as Select from '@radix-ui/react-select';
import { Check, ChevronDown, UserPlus } from 'lucide-react';
import type { RefObject } from 'react';

const CREATE_SENTINEL = '__create-personnel__';

export interface PersonSelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder: string;
  disabled?: boolean;
  invalid?: boolean;
  ariaDescribedBy?: string;
  /** `PERSONNEL_NOT_FOUND` sonrası tetikleyiciye odak taşımak için (bkz. `AnalysisReviewPane`). */
  triggerRef?: RefObject<HTMLButtonElement | null>;
  /** C9 — verilirse liste altına "+ Yeni personel oluştur" sentinel satırı eklenir. */
  createOption?: { label: string; onSelect: () => void };
}

export function PersonSelect({
  id,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  invalid,
  ariaDescribedBy,
  triggerRef,
  createOption,
}: PersonSelectProps) {
  return (
    <Select.Root
      // `''` KONTROLLÜ kalır ve placeholder'ı gösterir; `undefined` Radix'te kontrolsüz moda düşer
      // (temizlenen değer eski adı ekranda tutar).
      value={value}
      onValueChange={(next) => {
        if (next === CREATE_SENTINEL) {
          createOption?.onSelect();
          return;
        }
        onChange(next);
      }}
      disabled={disabled}
    >
      <Select.Trigger
        id={id}
        ref={triggerRef}
        aria-describedby={ariaDescribedBy}
        aria-invalid={invalid || undefined}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-md border bg-surface px-3 text-sm text-fg',
          'transition-colors data-[placeholder]:text-muted',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-primary',
          'disabled:cursor-not-allowed disabled:opacity-60',
          invalid ? 'border-danger focus-visible:ring-danger' : 'border-border-strong',
        )}
      >
        <Select.Value placeholder={placeholder} />
        <Select.Icon>
          <ChevronDown className="h-4 w-4 text-muted" aria-hidden="true" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          position="popper"
          sideOffset={4}
          className="z-[var(--z-dropdown)] max-h-[16rem] w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-border bg-surface shadow-md"
        >
          <Select.Viewport className="p-1">
            {options.map((opt) => (
              <Select.Item
                key={opt.value}
                value={opt.value}
                className="relative flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 pr-8 text-sm text-fg outline-none data-[highlighted]:bg-surface-muted"
              >
                <Select.ItemText>{opt.label}</Select.ItemText>
                <Select.ItemIndicator className="absolute right-2 inline-flex">
                  <Check className="h-4 w-4 text-primary" aria-hidden="true" />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.Viewport>
          {createOption ? (
            <>
              <Select.Separator className="mx-1 my-1 h-px bg-border" />
              <div className="p-1">
                <Select.Item
                  value={CREATE_SENTINEL}
                  className="relative flex cursor-pointer select-none items-center gap-1.5 rounded px-2 py-1.5 text-sm font-medium text-fg outline-none data-[highlighted]:bg-surface-muted"
                >
                  <UserPlus className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <Select.ItemText>{createOption.label}</Select.ItemText>
                </Select.Item>
              </div>
            </>
          ) : null}
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
